"use client"

import type { PropertyImageResponseDto } from "@/common/api/datasources/Api"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { GripVertical, ImageIcon, Loader2, X } from "lucide-react"
import { type CSSProperties, useMemo } from "react"

interface SortableImageItemProps {
  image: PropertyImageResponseDto
  isEditing: boolean
  isDeleting: boolean
  selectedImage: string | null
  onDeleteClick: (imageUUID: string) => void
  isPrimary?: boolean
}

export const SortableImageItem = ({
  image,
  isEditing,
  isDeleting,
  selectedImage,
  onDeleteClick,
  isPrimary = false,
}: SortableImageItemProps) => {
  if (!image?.fullSize?.url) return null

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: image.imageUUID,
    transition: {
      duration: 150,
      easing: "cubic-bezier(0.25, 1, 0.5, 1)",
    },
  })

  const style: CSSProperties = useMemo(
    () => ({
      transform: CSS.Transform.toString(transform),
      transition,
      zIndex: isDragging ? 2 : 1,
      userSelect: "none",
      WebkitUserSelect: "none",
      touchAction: "manipulation",
    }),
    [transform, transition, isDragging],
  )

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "relative group aspect-square rounded-lg overflow-hidden select-none touch-manipulation bg-muted/50",
        isDragging && "scale-[1.02] shadow-xl ring-2 ring-primary",
        !isDragging && "hover:ring-2 hover:ring-primary/50 transition-all duration-200",
        isPrimary && "ring-2 ring-primary",
      )}
    >
      {/* Image with fallback */}
      <div className="w-full h-full bg-muted">
        {image.fullSize.url ? (
          <img
            src={image.fullSize.url || "/placeholder.svg"}
            alt=""
            className="w-full h-full object-cover pointer-events-none transition-transform duration-300 group-hover:scale-105"
            draggable={false}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-muted">
            <ImageIcon className="h-10 w-10 text-muted-foreground/50" />
          </div>
        )}
      </div>

      {/* Primary badge */}
      {isPrimary && (
        <div className="absolute top-2 left-2 bg-primary text-primary-foreground text-xs font-medium py-1 px-2 rounded-md shadow-sm opacity-70">
          Primary
        </div>
      )}

      {/* Edit overlay */}
      {isEditing && (
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
          <Button
            variant="destructive"
            size="icon"
            className="absolute top-2 right-2 z-10 shadow-md hover:shadow-lg transition-all h-6 w-6"
            onClick={() => onDeleteClick(image.imageUUID)}
            disabled={isDeleting && selectedImage === image.imageUUID}
          >
            {isDeleting && selectedImage === image.imageUUID ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <X className="h-2 w-2" />
            )}
          </Button>
          <div
            {...attributes}
            {...listeners}
            className="absolute inset-0 cursor-grab active:cursor-grabbing flex items-center justify-center"
          >
            <div className="bg-black/40 rounded-full p-3 backdrop-blur-sm">
              <GripVertical className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </div>
        </div>
      )}

      {/* Image loading overlay */}
      <div
        className={cn(
          "absolute inset-0 bg-background/80 flex items-center justify-center",
          "opacity-0 pointer-events-none transition-opacity duration-300",
          isDeleting && selectedImage === image.imageUUID && "opacity-100",
        )}
      >
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
      </div>
    </div>
  )
}

